
import { Component, Vue } from 'vue-property-decorator'
import {
  getBucketGiftTierList,
  addBucketGiftTier,
  updateBucketGiftTier,
  deleteBucketGiftTier,
  setBucketGiftTierEnabled,
  bucketGiftTierSetSort
} from '@/api/bucketGiftTier'
import { listMyStores } from '@/api/store'
import { UserModule } from '@/store/modules/user'
import SortInlineEdit from '@/components/SortInlineEdit/index.vue'
import { scheduleElTableFixedSync } from '@/utils/elTableFixedSync'

@Component({
  name: 'BucketGiftTier',
  components: { SortInlineEdit }
})
export default class extends Vue {
  private loading = false
  private tableData: any[] = []
  private storeOptions: Array<{ id: number; name: string }> = []
  private dialogVisible = false
  private dialogTitle = '新增档位'
  private form: any = this.createEmptyForm()

  mounted() {
    this.loadStores()
    this.loadList()
    window.addEventListener('resize', this.scheduleTableLayout)
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.scheduleTableLayout)
  }

  private scheduleTableLayout() {
    scheduleElTableFixedSync(this, 'mainTable')
  }

  private getCurrentStoreId(): number | null {
    const sid = UserModule.storeId
    if (!sid) return null
    const n = Number(sid)
    return Number.isNaN(n) ? null : n
  }

  private get currentStoreLabel(): string {
    const sid = this.getCurrentStoreId()
    if (sid == null) return '未选择商户'
    const found = this.storeOptions.find((s) => Number(s.id) === sid)
    return found ? found.name : `店铺#${sid}`
  }

  private onlyCurrentStoreRows(list: any[]): any[] {
    const sid = this.getCurrentStoreId()
    if (sid == null) return []
    return (list || []).filter(
      (row) => row && row.storeId != null && Number(row.storeId) === sid
    )
  }

  private createEmptyForm() {
    const current = this.getCurrentStoreId()
    return {
      id: null,
      storeId: current,
      minTotalBuckets: '',
      giftBuckets: '',
      sort: '0',
      enabledSwitch: true
    }
  }

  private async loadStores() {
    try {
      const res: any = await listMyStores()
      if (String(res.data.code) === '1' && Array.isArray(res.data.data)) {
        this.storeOptions = res.data.data.map((it: any) => ({
          id: it.id,
          name: it.storeName || it.name || `店铺${it.id}`
        }))
      }
    } catch (e) {
      this.storeOptions = []
    }
  }

  private formatStoreLabel(storeId: number | null | undefined) {
    if (storeId == null || storeId === 0) {
      return '（历史全局，已隐藏）'
    }
    const found = this.storeOptions.find(s => s.id === storeId)
    return found ? found.name : `店铺${storeId}`
  }

  private async loadList() {
    this.loading = true
    try {
      const res: any = await getBucketGiftTierList()
      if (String(res.data.code) === '1') {
        this.tableData = this.onlyCurrentStoreRows(res.data.data || [])
      }
    } catch (e) {
      this.$message.error('加载失败')
    } finally {
      this.loading = false
      this.scheduleTableLayout()
    }
  }

  private setSortHandle(row: any, sort: number) {
    if (!row || !row.id) return
    bucketGiftTierSetSort({ id: row.id, sort })
      .then((res: any) => {
        if (res && res.data && String(res.data.code) === '1') {
          this.$message.success('排序已更新')
          this.loadList()
        } else {
          this.$message.error((res.data && (res.data.msg || res.data.desc)) || '排序失败')
        }
      })
      .catch((err: any) => {
        this.$message.error('请求出错了：' + ((err && err.message) || '未知错误'))
      })
  }

  private openDialog(row?: any) {
    const current = this.getCurrentStoreId()
    if (!current) {
      this.$message.warning('请先在顶栏选择商户')
      return
    }
    if (row) {
      this.dialogTitle = '修改档位'
      this.form = {
        id: row.id,
        storeId: current,
        minTotalBuckets: String(row.minTotalBuckets != null ? row.minTotalBuckets : ''),
        giftBuckets: String(row.giftBuckets != null ? row.giftBuckets : ''),
        sort: String(row.sort != null ? row.sort : 0),
        enabledSwitch: row.enabled === 1
      }
    } else {
      this.dialogTitle = '新增档位'
      this.form = this.createEmptyForm()
    }
    this.dialogVisible = true
  }

  private async submit() {
    const storeId = this.getCurrentStoreId()
    if (!storeId) {
      this.$message.warning('请先在顶栏选择商户')
      return
    }
    const minTotalBuckets = Number(this.form.minTotalBuckets)
    const giftBuckets = Number(this.form.giftBuckets)
    if (!minTotalBuckets || minTotalBuckets <= 0) {
      this.$message.warning('请填写有效的累计购买桶数下限')
      return
    }
    if (isNaN(giftBuckets) || giftBuckets < 0) {
      this.$message.warning('请填写有效的赠送桶数')
      return
    }
    const payload: any = {
      minTotalBuckets,
      giftBuckets,
      enabled: this.form.enabledSwitch ? 1 : 0,
      sort: this.form.id ? Number(this.form.sort || 0) : null,
      storeId
    }
    try {
      if (this.form.id) {
        payload.id = this.form.id
        await updateBucketGiftTier(payload)
      } else {
        await addBucketGiftTier(payload)
      }
      this.$message.success('保存成功')
      this.dialogVisible = false
      this.loadList()
    } catch (e) {
      this.$message.error('保存失败')
    }
  }

  private async toggleEnabled(row: any) {
    const enabled = row.enabled === 1 ? 0 : 1
    try {
      await setBucketGiftTierEnabled(row.id, enabled)
      this.$message.success('操作成功')
      this.loadList()
    } catch (e) {
      this.$message.error('操作失败')
    }
  }

  private deleteRow(row: any) {
    this.$confirm('确定删除该赠送档位？', '提示', { type: 'warning' })
      .then(async () => {
        await deleteBucketGiftTier(row.id)
        this.$message.success('已删除')
        this.loadList()
      })
      .catch(() => {})
  }
}
